import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DocumentArrowDownIcon, FunnelIcon } from '@heroicons/react/24/outline'
import WorkersByGroupChart from 'components/charts/WorkersByGroupChart'
import WorkersInBlockChart from 'components/charts/WorkersInBlockChart'
import TableComponent from 'components/common/TableComponent'
import ModalComponent from 'components/common/ModalComponent'
import ButtonComponent from 'components/common/ButtonComponent'
import PageLoadingState from 'components/common/PageLoadingState'
import DateRangeFilterModal from 'components/modals/DateRangeFilterModal'
import { useWorkdaySummary } from 'hooks/useWorkdaySummary'
import { useDashboardFilterOptions } from 'hooks/useDashboardFilterOptions'
import { parseDate } from 'utils'
import { createDefaultDateRange, getDateRangeSummary } from 'utils/dateRangeFilters'
import { exportDataEditExcel } from 'utils/dataEditExport'

const workersByGroupDetailColumns = [
  { key: 'workerId', labelKey: 'columns.workerId' },
  { key: 'workerName', labelKey: 'columns.workerName' },
  { key: 'groupName', labelKey: 'columns.groupName' },
  { key: 'lastWorkingDay', labelKey: 'columns.lastWorkingDay' },
  { key: 'startTime', labelKey: 'columns.startTime' }
]

const workersInBlockDetailColumns = [
  { key: 'workerId', labelKey: 'columns.workerId' },
  { key: 'workerName', labelKey: 'columns.workerName' },
  { key: 'blockName', labelKey: 'columns.blockName' },
  { key: 'deviceGroupName', labelKey: 'columns.deviceGroupName' },
  { key: 'eventTime', labelKey: 'columns.eventTime' }
]

// Export columns carry a resolved `label` (what appears as the spreadsheet
// header) built from the display columns' i18n keys.
const toExportColumns = (columns, t) => columns.map(col => ({ key: col.key, label: t(col.labelKey) }))

const toFileNamePart = value => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '') || 'details'

const FarmActivitySummaryTable = ({ farmActivityData }) => {
  const tableData = {
    columns: [
      { key: 'blockName', labelKey: 'columns.blockName' },
      { key: 'groupName', labelKey: 'columns.groupName' },
      { key: 'cropName', labelKey: 'columns.crop' },
      { key: 'varietyName', labelKey: 'columns.variety' },
      { key: 'numberOfPlants', labelKey: 'columns.total' },
      { key: 'workerCount', labelKey: 'columns.workersNumber' },
      { key: 'firstScan', labelKey: 'columns.firstScan', cell: (value) => parseDate(value) },
      { key: 'lastScan', labelKey: 'columns.lastScan', cell: (value) => parseDate(value) }
    ],
    rows: farmActivityData
  }
  return <TableComponent tableData={tableData} defaultSortColumn={tableData.columns[0]?.key} />
}

const LastActivitiesBoardTable = ({ lastActivitiesData }) => {
  const tableData = {
    columns: [
      // Workers are still identified by their legacy id (old_id); fall back to
      // the internal worker_id only when a worker has no legacy id.
      { key: 'workerId', labelKey: 'columns.workerId', cell: (value, row) => row.oldId || row.workerId },
      { key: 'workerName', labelKey: 'columns.workerName' },
      { key: 'lastActivity', labelKey: 'columns.lastActivity' },
      { key: 'timePassed', labelKey: 'columns.timePassed' },
      { key: 'blockName', labelKey: 'columns.blockName' },
      { key: 'location', labelKey: 'columns.location', cell: (value) => value ? value.split(',').slice(0, 2).join(', ') : '' }
    ],
    rows: lastActivitiesData
  }
  return <TableComponent tableData={tableData} defaultSortColumn={tableData.columns[0]?.key} />
}

const getGroupWorkers = (group, lastActivitiesData = []) => {
  if (!group) return []

  const groupWorkers = [
    group.workers,
    group.workersList,
    group.workerList,
    group.workerDetails,
    group.workersDetails,
    group.workerData,
    group.workersData,
    group.details,
    group.items,
    group.data
  ].find(value => Array.isArray(value) || Array.isArray(value?.workers) || Array.isArray(value?.items))

  let workers = lastActivitiesData.filter(worker => worker.groupName === group.groupName)
  if (Array.isArray(groupWorkers)) {
    workers = groupWorkers
  } else if (Array.isArray(groupWorkers?.workers)) {
    workers = groupWorkers.workers
  } else if (Array.isArray(groupWorkers?.items)) {
    workers = groupWorkers.items
  }

  return workers.map(worker => ({
    // Legacy id (old_id) takes priority; internal worker_id is the fallback.
    workerId: worker.oldId || worker.old_id || worker.workerId || worker.worker_id || '',
    workerName: worker.workerName || worker.worker_name || worker.name || '',
    groupName: worker.groupName || worker.group_name || group.groupName || group.group_name || '',
    activityName: worker.activityName || worker.activity_name || worker.lastActivity || worker.last_activity || '',
    lastWorkingDay: worker.lastWorkingDay || worker.last_working_day || worker.date || worker.createdAt || worker.created_at || '',
    startTime: worker.startTime || worker.start_time || ''
  }))
}

const getBlockWorkers = (block, lastActivitiesData = []) => {
  if (!block) return []

  const blockWorkers = [
    block.workers,
    block.workersList,
    block.workerList,
    block.workerDetails,
    block.workersDetails,
    block.workerData,
    block.workersData,
    block.details,
    block.items,
    block.data
  ].find(value => Array.isArray(value) || Array.isArray(value?.workers) || Array.isArray(value?.items))

  let workers = lastActivitiesData.filter(worker => worker.blockName === block.blockName)
  if (Array.isArray(blockWorkers)) {
    workers = blockWorkers
  } else if (Array.isArray(blockWorkers?.workers)) {
    workers = blockWorkers.workers
  } else if (Array.isArray(blockWorkers?.items)) {
    workers = blockWorkers.items
  }

  return workers.map(worker => ({
    // Legacy id (old_id) takes priority; internal worker_id is the fallback.
    workerId: worker.oldId || worker.old_id || worker.workerId || worker.worker_id || '',
    workerName: worker.workerName || worker.worker_name || worker.name || '',
    blockName: worker.blockName || worker.block_name || block.blockName || block.block_name || '',
    activityName: worker.activityName || worker.activity_name || worker.lastActivity || worker.last_activity || '',
    deviceGroupName: worker.deviceGroupName || worker.device_group_name || worker.groupName || worker.group_name || '',
    eventTime: worker.eventTime || worker.event_time || worker.createdAt || worker.created_at || worker.time || ''
  }))
}

const WorkersByGroupDetailsTable = ({ group, lastActivitiesData }) => {
  const tableData = {
    columns: workersByGroupDetailColumns,
    rows: getGroupWorkers(group, lastActivitiesData)
  }

  return <TableComponent tableData={tableData} defaultSortColumn={tableData.columns[0]?.key} />
}

const WorkersInBlockDetailsTable = ({ block, lastActivitiesData }) => {
  const tableData = {
    columns: workersInBlockDetailColumns,
    rows: getBlockWorkers(block, lastActivitiesData)
  }

  return <TableComponent tableData={tableData} defaultSortColumn={tableData.columns[0]?.key} />
}

const LastDayWorkingSummary = () => {
  const { t } = useTranslation()
  const { data, updateDateRange } = useWorkdaySummary()
  const filterOptions = useDashboardFilterOptions(data.farmId)
  const [selectedGroup, setSelectedGroup] = useState(null)
  const [selectedBlock, setSelectedBlock] = useState(null)
  const [showDateRangeModal, setShowDateRangeModal] = useState(false)
  const dateRange = data.dateRange || createDefaultDateRange(data.filterDate)

  const closeGroupDetailsModal = () => {
    setSelectedGroup(null)
  }

  const closeBlockDetailsModal = () => {
    setSelectedBlock(null)
  }

  const exportGroupDetails = () => {
    if (!selectedGroup) return

    exportDataEditExcel({
      rows: getGroupWorkers(selectedGroup, data.lastActivitiesData),
      columns: toExportColumns(workersByGroupDetailColumns, t),
      fileName: `workers-in-${toFileNamePart(selectedGroup.groupName || 'group')}-${data.currentDate}`,
      sheetName: t('dashboard.lastDay.workersIn', { name: selectedGroup.groupName || t('common.group') })
    })
  }

  const exportBlockDetails = () => {
    if (!selectedBlock) return

    exportDataEditExcel({
      rows: getBlockWorkers(selectedBlock, data.lastActivitiesData),
      columns: toExportColumns(workersInBlockDetailColumns, t),
      fileName: `workers-in-${toFileNamePart(selectedBlock.blockName || 'block')}-${data.currentDate}`,
      sheetName: t('dashboard.lastDay.workersIn', { name: selectedBlock.blockName || t('common.block') })
    })
  }

  if (data.loading) {
    return <PageLoadingState message={t('dashboard.lastDay.loading')} rows={12} />
  }

  return (
    <div className='p-6 bg-white-50 min-h-screen'>
      <div className='flex justify-between items-start mb-4'>
        <div>
          <h1 className='text-2xl font-semibold text-gray-600'>{t('dashboard.lastDay.title')}</h1>
        </div>
        <div className='flex items-center gap-2'>
          <span className='text-sm text-gray-700'>{getDateRangeSummary(dateRange)}</span>
          <ButtonComponent className='flex items-center gap-2' onClick={() => setShowDateRangeModal(true)}>
            <FunnelIcon className='w-5 h-5' />
            <span>{t('dashboard.advancedFilters')}</span>
          </ButtonComponent>
        </div>
      </div>
      <hr className='my-8 border-gray-300' />
      <div className='flex gap-6'>
        <section className='w-1/2'>
          <h1 className='text-2xl text-gray-500 mb-4'>{t('dashboard.lastDay.workersByGroup')}</h1>
          <p className='text-gray-500'>{t('dashboard.lastDay.workingRange', { range: getDateRangeSummary(dateRange) })}</p>
          {data.loading
            ? (
              <div className='w-full h-96 min-h-[600px] p-6 flex items-center justify-center'>
                <div className='text-gray-500'>{t('common.loadingChart')}</div>
              </div>
              )
            : (
              <WorkersByGroupChart
                data={data.workersByGroup}
                totalWorkers={data.totalWorkers}
                onGroupClick={setSelectedGroup}
              />
              )}
        </section>
        <section className='w-1/2'>
          <h1 className='text-2xl text-gray-500 mb-4'>{t('dashboard.lastDay.workersInBlock')}</h1>
          <p className='text-gray-500'>{t('dashboard.lastDay.workingRange', { range: getDateRangeSummary(dateRange) })}</p>
          {data.loading
            ? (
              <div className='w-full h-96 min-h-[600px] p-6 flex items-center justify-center'>
                <div className='text-gray-500'>{t('common.loadingChart')}</div>
              </div>
              )
            : (
              <WorkersInBlockChart data={data.workersInBlock} onBlockClick={setSelectedBlock} />
              )}
        </section>
      </div>
      <section className='mt-12'>
        <h1 className='text-2xl text-gray-500 mb-4'>{t('dashboard.lastDay.farmActivitySummary')}</h1>
        {data.loading
          ? (
            <div className='text-gray-500'>{t('dashboard.lastDay.loadingFarmActivity')}</div>
            )
          : data.error
            ? (
              <div className='text-red-500'>{data.error}</div>
              )
            : (
              <FarmActivitySummaryTable farmActivityData={data.farmActivityData} />
              )}
      </section>
      <section className='mt-12'>
        <h1 className='text-2xl text-gray-500 mb-4'>{t('dashboard.lastDay.lastActivitiesBoard')}</h1>
        {data.loading
          ? (
            <div className='text-gray-500'>{t('dashboard.lastDay.loadingLastActivities')}</div>
            )
          : data.error
            ? (
              <div className='text-red-500'>{data.error}</div>
              )
            : (
              <LastActivitiesBoardTable lastActivitiesData={data.lastActivitiesData} />
              )}
      </section>
      <ModalComponent
        isOpen={Boolean(selectedGroup)}
        onClose={closeGroupDetailsModal}
        title={t('dashboard.lastDay.workersIn', { name: selectedGroup?.groupName || t('common.group') })}
        maxWidth='5xl'
        customFooter={(
          <>
            <ButtonComponent type='button' onClick={exportGroupDetails}>
              <DocumentArrowDownIcon className='w-5 h-5' />
              <span>{t('common.exportExcel')}</span>
            </ButtonComponent>
            <button
              type='button'
              onClick={closeGroupDetailsModal}
              className='px-4 py-2 rounded bg-gray-100 text-greenPrimary hover:bg-gray-200'
            >
              {t('common.close')}
            </button>
          </>
        )}
      >
        <WorkersByGroupDetailsTable group={selectedGroup} lastActivitiesData={data.lastActivitiesData} />
      </ModalComponent>
      <ModalComponent
        isOpen={Boolean(selectedBlock)}
        onClose={closeBlockDetailsModal}
        title={t('dashboard.lastDay.workersIn', { name: selectedBlock?.blockName || t('common.block') })}
        maxWidth='6xl'
        customFooter={(
          <>
            <ButtonComponent type='button' onClick={exportBlockDetails}>
              <DocumentArrowDownIcon className='w-5 h-5' />
              <span>{t('common.exportExcel')}</span>
            </ButtonComponent>
            <button
              type='button'
              onClick={closeBlockDetailsModal}
              className='px-4 py-2 rounded bg-gray-100 text-greenPrimary hover:bg-gray-200'
            >
              {t('common.close')}
            </button>
          </>
        )}
      >
        <WorkersInBlockDetailsTable block={selectedBlock} lastActivitiesData={data.lastActivitiesData} />
      </ModalComponent>
      <DateRangeFilterModal
        isOpen={showDateRangeModal}
        dateRange={dateRange}
        sectors={filterOptions.sectors}
        farmingBlocks={filterOptions.farmingBlocks}
        groups={filterOptions.groups}
        activities={filterOptions.activities}
        maxDate={data.currentDate}
        onClose={() => setShowDateRangeModal(false)}
        onApply={(nextRange) => {
          updateDateRange(nextRange)
          setShowDateRangeModal(false)
        }}
      />
    </div>
  )
}

export default LastDayWorkingSummary
